import { FormEvent, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  useCreateAssessorMutation,
  useSearchAssessorMembersQuery,
} from "../api/assessorsApi";
import {
  ArrowLeft,
  UserPlus,
  Search,
  User,
  ShieldCheck,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

// Security & UI Patterns
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

const API_BASE = "https://api.niqsng.org";

const buildPhotoUrl = (proPic?: string | null) => {
  if (!proPic) return null;
  if (proPic.startsWith("http://") || proPic.startsWith("https://"))
    return proPic;
  return `${API_BASE}/storage/${proPic.replace(/^\/+/, "")}`;
};

const fullName = (m: any) =>
  [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—";

export default function AssessorCreatePage() {
  const nav = useNavigate();
  const [create, { isLoading }] = useCreateAssessorMutation();

  // search UI state
  const [query, setQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<any | null>(null);

  // workload
  const [maxWorkload, setMaxWorkload] = useState("10");

  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // only search when query length >= 2 and no member selected
  const shouldSearch = query.trim().length >= 2 && !selectedMember;

  const { data: searchRes, isFetching } = useSearchAssessorMembersQuery(query.trim(), {
    skip: !shouldSearch,
  });

  // normalize server response
  const members = useMemo(() => {
    if (!searchRes) return [];
    if ((searchRes as any)?.data) return (searchRes as any).data;
    return searchRes as any[];
  }, [searchRes]);

  const photoUrl = useMemo(() => {
    const p = selectedMember?.user?.pro_pic ?? selectedMember?.pro_pic ?? null;
    return buildPhotoUrl(p);
  }, [selectedMember]);

  const pickMember = (m: any) => {
    setSelectedMember(m);
    setQuery("");
    setMsg(null);
  };

  const clearSelected = () => {
    setSelectedMember(null);
    setQuery("");
    setMsg(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    // Search results expose `id` = user_id and `member_id` = the member UUID.
    // The backend expects the member UUID, so send that (fall back to id).
    const memberId = selectedMember?.member_id ?? selectedMember?.id;
    if (!memberId) {
      setMsg({ type: "error", text: "Please select a member first." });
      return;
    }

    try {
      const res = await create({
        member_id: memberId,
        max_workload: maxWorkload ? Number(maxWorkload) : undefined,
      }).unwrap();

      if (res?.status && res.status !== "success") {
        throw { data: { message: res?.message || "Create failed" } };
      }

      setMsg({
        type: "success",
        text: res?.message || "Assessor created successfully",
      });
      setTimeout(() => nav("/assessors"), 1200);
    } catch (err: any) {
      setMsg({ type: "error", text: err?.data?.message || "Create failed" });
    }
  };

  return (
    <PageGate perm={PERMS.assessors.create}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header/Breadcrumb */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav("/assessors")}
              className="p-2 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Commission Assessor
              </h1>
              <p className="text-sm text-gray-500">
                Search member registry to assign assessor privileges
              </p>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {msg && (
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm animate-in fade-in slide-in-from-top-2 ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            {msg.type === "success" ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{msg.text}</span>
          </div>
        )}

        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <form onSubmit={onSubmit} className="p-6 space-y-8">
            {/* Step 1: Member Identification */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-[10px] font-bold">
                  1
                </div>
                <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Select Member
                </h3>
              </div>

              <div className="relative">
                {!selectedMember ? (
                  <div className="relative group">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      autoFocus
                      className="w-full pl-10 pr-4 py-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 transition-all"
                      placeholder="Type membership number, name, or email..."
                    />

                    {/* Dropdown Results */}
                    {shouldSearch && (
                      <div className="absolute z-30 mt-2 w-full bg-white border rounded-xl shadow-lg overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                        <div className="px-4 py-2 bg-gray-50 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b flex justify-between">
                          <span>
                            {isFetching
                              ? "Syncing Registry..."
                              : `${members.length} Match(es) Found`}
                          </span>
                        </div>

                        {members.length ? (
                          <div className="max-h-80 overflow-y-auto">
                            {members.map((m: any) => (
                              <button
                                type="button"
                                key={m.id}
                                onClick={() => pickMember(m)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b last:border-0 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                                  {m.firstname?.[0]}
                                  {m.surname?.[0]}
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-gray-800">
                                    {fullName(m)}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    #{m.membership_no || "N/A"} •{" "}
                                    {m.email || "No Email"}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="px-4 py-8 text-center">
                            {isFetching ? (
                              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-300" />
                            ) : (
                              <p className="text-sm text-gray-500 italic">
                                No members found
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Selected Member Profile Card */
                  <div className="border-2 border-gray-900/5 bg-gray-50/50 rounded-2xl p-5 flex items-center justify-between gap-4 animate-in slide-in-from-left-4">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-white flex items-center justify-center">
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt="Assessor"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-8 h-8 text-gray-200" />
                          )}
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-green-500 border-2 border-white w-4 h-4 rounded-full"></div>
                      </div>

                      <div>
                        <div className="text-lg font-bold text-gray-900">
                          {fullName(selectedMember)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <span className="px-2 py-0.5 bg-white border rounded-md font-mono">
                            #{selectedMember.membership_no}
                          </span>
                          <span className="h-1 w-1 bg-gray-300 rounded-full"></span>
                          <span>{selectedMember.email}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={clearSelected}
                      className="p-2 hover:bg-white hover:text-red-600 rounded-lg transition-all text-gray-400"
                      title="Clear Selection"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Configuration */}
            <div className="pt-6 border-t border-dashed">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-[10px] font-bold">
                  2
                </div>
                <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Workload Configuration
                </h3>
              </div>

              <div className="max-w-xs">
                <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1.5 ml-1">
                  Maximum Logbooks
                </label>
                <input
                  type="number"
                  value={maxWorkload}
                  onChange={(e) => setMaxWorkload(e.target.value)}
                  className="w-full border rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gray-100 font-semibold"
                  placeholder="e.g. 10"
                />
                <p className="mt-2 text-[10px] text-gray-400 leading-relaxed italic">
                  Defines the total capacity of logbooks this assessor can
                  manage simultaneously.
                </p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="pt-8 flex items-center justify-between border-t border-gray-100">
              <Link
                to="/assessors"
                className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
              >
                Cancel and return
              </Link>

              <button
                type="submit"
                disabled={isLoading || !selectedMember}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold shadow-sm hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Create Assessor
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </PageGate>
  );
}
